<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditVideo.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditVideo.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditVideo.title') }}</li>
                    </ol>
                </div>
            </div>

            <div class="row pb-4">
                <div class="col-xxl-8 col-xl-10">
                    <!-- Ajout d'un équipement vidéo : la marque est choisie ensuite -->
                    <div class="card mb-3">
                        <div class="card-header pb-0">
                            <h4 class="header-title">{{ $t('componentEditVideo.addSection') }}</h4>
                        </div>
                        <div class="card-body d-flex flex-wrap gap-2">
                            <div v-for="action in addActions" :key="action.type" class="dropdown">
                                <button :id="`add-${action.type}`" type="button" class="btn btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                                    <i :class="['mdi', action.icon, 'me-1']"></i>{{ action.label }}
                                </button>
                                <div class="dropdown-menu">
                                    <h6 class="dropdown-header">{{ $t('componentEditVideo.chooseBrand') }}</h6>
                                    <a v-for="brand in brands" :key="brand" class="dropdown-item" href="javascript:void(0);" @click="addDevice(action.type, brand)">{{ brand }}</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Equipements déclarés sur le LINK -->
                    <div class="card">
                        <div class="card-header pb-0 d-flex align-items-center justify-content-between">
                            <h4 class="header-title">{{ $t('componentEditVideo.declaredSection') }}</h4>
                            <button id="video-refresh" type="button" class="btn btn-sm btn-light border" :disabled="loading"
                                :title="$t('common.refresh')" :aria-label="$t('common.refresh')" @click="load">
                                <i class="mdi mdi-refresh"></i>
                            </button>
                        </div>
                        <div class="card-body pt-2">
                            <div v-if="loading" class="d-flex justify-content-center py-3">
                                <div class="spinner-border text-primary" role="status"></div>
                            </div>
                            <p v-else-if="loadError" class="text-danger text-center py-3 mb-0">{{ loadError }}</p>
                            <p v-else-if="devices.length === 0" class="text-center py-3 mb-0">{{ $t('componentEditVideo.noDevice') }}</p>
                            <div v-else class="table-responsive">
                                <table id="video-devices" class="table table-centered mb-0">
                                    <thead>
                                        <tr>
                                            <th>{{ $t('installationComponents.legend.component') }}</th>
                                            <th>{{ $t('installationComponents.legend.type') }}</th>
                                            <th>{{ $t('installationComponents.legend.state') }}</th>
                                            <th class="text-end">{{ $t('installationComponents.legend.actions') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="device in devices" :key="device.key">
                                            <td class="device-name">
                                                <i :class="['mdi', device.icon, 'text-success', 'font-18', 'me-1']"></i>
                                                <a href="javascript:void(0);" class="fw-bold text-dark" @click="editDevice(device)">{{ device.name }}</a>
                                            </td>
                                            <td class="device-type fst-italic text-muted">{{ subtitle(device) }}</td>
                                            <td class="device-state"><strong :class="stateClass(device)">{{ device.stateLabel }}</strong></td>
                                            <td class="text-end">
                                                <button type="button" class="btn btn-sm btn-light border device-edit" @click="editDevice(device)">
                                                    <i class="mdi mdi-pencil"></i><span class="d-none d-sm-inline ms-1">{{ $t('common.edit') }}</span>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                <router-link :to="pathToInstallationEditComponents" class="btn btn-outline-secondary">{{ $t('common.back') }}</router-link>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/userStore';
import { VIDEO_BRANDS } from '@/models/video';

// Video integration of a LINK: NVRs and cameras declared on it, and the addition of new ones.
// The devices are not part of the hub payload, they are loaded when the page opens.
export default {
    name: 'ComponentEditVideo',
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.hubId);
        const hub = userStore.getHub(facilityId, hubId);
        const loading = ref(true);
        const loadError = ref(null);

        // NVRs first, then cameras, as in the app
        const devices = computed(() => [...hub.nvrs, ...hub.cameras]);

        const addActions = computed(() => [
            { type: 'nvr', label: t('componentEditVideo.addNvr'), icon: 'mdi-harddisk' },
            { type: 'camera', label: t('componentEditVideo.addCamera'), icon: 'mdi-video-outline' },
        ]);

        const load = async () => {
            loading.value = true;
            loadError.value = null;
            try {
                await userStore.loadVideoDevices(facilityId, hub);
            } catch (message) {
                loadError.value = message;
            } finally {
                loading.value = false;
            }
        };

        const devicePath = (query) => ({ path: '/component-edit-video-device', query: { facilityId, hubId, ...query } });

        const addDevice = (type, brand) => {
            router.push(devicePath({ type, brand }));
        };

        const editDevice = (device) => {
            router.push(devicePath({ type: device.type, id: device.id }));
        };

        // A camera on an NVR shows the NVR name, the other devices their address
        const subtitle = (device) => {
            if (device.type === 'camera' && device.isOnNvr) {
                const nvr = hub.nvrs.find(n => n.id === device.nvr_id);
                return nvr ? `${device.typeLabel} ${nvr.name}` : device.typeLabel;
            }
            return `${device.typeLabel} - ${device.ip}`;
        };

        const stateClass = (device) => {
            if (device.isConnecting) return 'text-muted';
            return device.isActive ? 'text-success' : 'text-danger';
        };

        const pathToInstallationEditComponents = computed(() => {
            return `/installation-edit?id=${facilityId}&action=components`;
        });

        onMounted(load);

        return {
            brands: VIDEO_BRANDS,
            addActions,
            devices,
            loading,
            loadError,
            load,
            addDevice,
            editDevice,
            subtitle,
            stateClass,
            pathToInstallationEditComponents,
        };
    },
};
</script>
